import { FileSearch, BookOpen, CalendarCheck, Compass, Users, Blocks, ArrowRight, MessageCircleQuestion } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { prefillMotivo } from "../config/site";

const services = [
  {
    icon: FileSearch,
    title: "Evaluación psicopedagógica",
    desc: "Evaluación individualizada para conocer el perfil de aprendizaje, identificar fortalezas y dificultades y orientar la intervención educativa.",
    target: "Niños, niñas y adolescentes cuyas familias o docentes observan dificultades de aprendizaje y quieren comprender su origen.",
    img: "https://images.unsplash.com/photo-1560785496-3c9d27877182?auto=format&fit=crop&w=900&q=85",
    pos: "50% 60%",
    alt: "Niño trabajando con material de lectura y escritura durante una evaluación psicopedagógica individualizada",
  },
  {
    icon: BookOpen,
    title: "Reeducación pedagógica",
    desc: "Intervención individualizada para trabajar las dificultades de aprendizaje, desarrollar habilidades y mejorar la autonomía académica.",
    target: "Alumnado con dificultades persistentes en lectura, escritura o cálculo que necesita una intervención especializada.",
    img: "https://images.unsplash.com/photo-1758525862357-27fb39545240?auto=format&fit=crop&w=900&q=85",
    pos: "50% 35%",
    alt: "Profesional trabajando individualmente con una niña en actividades de aprendizaje",
  },
  {
    icon: CalendarCheck,
    title: "Técnicas de estudio y organización académica",
    desc: "Estrategias para planificar el estudio, organizar tareas, gestionar el tiempo, preparar exámenes y aprender de forma más eficaz.",
    target: "Estudiantes de primaria, ESO y bachillerato que quieren organizarse mejor y estudiar con autonomía.",
    img: "https://images.unsplash.com/photo-1517971129774-8a2b38fa128e?auto=format&fit=crop&w=900&q=85",
    pos: "50% 28%",
    alt: "Adolescente estudiando y organizando sus apuntes junto a una ventana con luz natural",
  },
  {
    icon: Compass,
    title: "Orientación académica",
    desc: "Acompañamiento a adolescentes en la toma de decisiones académicas, el conocimiento de sus intereses y capacidades y la planificación de su itinerario educativo.",
    target: "Adolescentes que deben elegir itinerario o estudios y necesitan orientación para decidir con confianza.",
    img: "https://images.unsplash.com/photo-1752650732799-6e81d5f4c398?auto=format&fit=crop&w=900&q=85",
    pos: "50% 40%",
    alt: "Orientadora conversando con una adolescente sobre sus intereses y opciones educativas",
  },
  {
    icon: Users,
    title: "Orientación familiar",
    desc: "Asesoramiento a las familias para comprender las necesidades educativas de sus hijos e hijas y disponer de pautas prácticas para acompañar su aprendizaje.",
    target: "Madres y padres que desean pautas claras para acompañar el aprendizaje en casa.",
    img: "https://images.unsplash.com/photo-1714976694810-85add1a29c96?auto=format&fit=crop&w=900&q=85",
    pos: "50% 35%",
    alt: "Profesional conversando con una madre en un ambiente cálido y acogedor",
  },
  {
    icon: Blocks,
    title: "Acompañamiento al desarrollo (0-6 años)",
    desc: "Observación y acompañamiento del desarrollo en los primeros años: lenguaje, atención, juego, autonomía y primeras habilidades para el aprendizaje, con pautas claras para la familia y coordinación con la escuela infantil.",
    target: "Familias con niños y niñas de 0 a 6 años que tienen dudas sobre su desarrollo o quieren acompañarlo mejor.",
    img: "https://images.pexels.com/photos/7296764/pexels-photo-7296764.jpeg?auto=format&fit=crop&w=900&q=85",
    pos: "50% 45%",
    alt: "Manos de una niña pequeña jugando con bloques de madera sobre una mesa, sin rostro visible",
  },
];

export default function Services() {
  return (
    <section id="servicios" data-testid="services-section" className="py-20 lg:py-28 bg-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeading
            eyebrow="Servicios"
            title="¿Cómo puedo ayudaros?"
            intro="Intervención psicopedagógica especializada, siempre desde una mirada individualizada y en coordinación con la familia."
          />
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.1}>
              <article
                data-testid={`service-card-${i}`}
                className="group bg-white rounded-3xl overflow-hidden border border-atento-blue/10 shadow-[0_4px_20px_rgba(27,59,111,0.04)] hover:shadow-[0_16px_40px_rgba(27,59,111,0.10)] hover:border-sage/40 transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.alt}
                    loading="lazy"
                    style={{ objectPosition: s.pos }}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-atento-deep/40 to-transparent" />
                  <span className="absolute top-4 left-4 font-serif italic text-ivory/90 text-lg">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-start gap-3">
                    <s.icon className="w-6 h-6 text-sage-dark shrink-0 mt-0.5" strokeWidth={1.8} />
                    <h3 className="font-accent font-semibold text-lg text-atento-blue leading-snug">
                      {s.title}
                    </h3>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-charcoal/70">{s.desc}</p>
                  <p className="mt-4 text-xs leading-relaxed text-charcoal/55 bg-sage-light/60 rounded-xl px-4 py-3">
                    <span className="font-accent font-semibold uppercase tracking-[0.1em] text-sage-dark block mb-1 text-[10px]">
                      Dirigido a
                    </span>
                    {s.target}
                  </p>
                  <button
                    onClick={() => prefillMotivo(`Hola Lidia, me gustaría recibir información sobre el servicio de ${s.title.toLowerCase()}.`)}
                    data-testid={`service-cta-${i}`}
                    className="mt-auto pt-6 inline-flex items-center gap-2 font-accent font-semibold text-sm text-atento-blue hover:text-atento-dark transition-colors cursor-pointer"
                  >
                    Consultar este servicio
                    <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div
            data-testid="services-help-card"
            className="mt-7 bg-atento-blue rounded-3xl p-8 sm:p-10 relative overflow-hidden"
          >
            <div
              aria-hidden="true"
              className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-sage/20 blur-2xl"
            />
            <div className="relative flex flex-col lg:flex-row lg:items-center gap-7 lg:gap-10">
              <div className="flex-1">
                <div className="flex items-center gap-4">
                  <MessageCircleQuestion className="w-8 h-8 text-sage shrink-0" strokeWidth={1.6} />
                  <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-ivory leading-snug">
                    ¿No sabes qué servicio necesita tu hijo/a?
                  </h3>
                </div>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-ivory/75 max-w-2xl">
                  Es lo más habitual. Cuéntame vuestra situación y en la primera consulta
                  vemos juntos el camino más adecuado, sin compromiso.
                </p>
              </div>
              <button
                onClick={() => prefillMotivo("Hola Lidia, no tengo claro qué servicio necesita mi hijo/a y me gustaría que me orientases.")}
                data-testid="services-help-cta"
                className="shrink-0 inline-flex items-center justify-center gap-2 bg-ivory text-atento-blue font-accent font-semibold text-sm px-7 py-4 rounded-full hover:bg-white transition-colors cursor-pointer"
              >
                Pedir orientación
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
